import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './CustomerDashboard.css';

// --- MODULES ---
import JDGenerator from '../Recruiter/JDGenerator';
import CustomerGeneratedJDs from './CustomerGeneratedJDs';
import JobPosting from '../Recruiter/JobPosting/JobPosting';
import PostedJobs from '../Recruiter/PostedJobs';
import RecruiterApplicants from '../Recruiter/JobPosting/RecruiterApplicants';
import MasterApplicants from '../Recruiter/JobPosting/MasterApplicants';
import CustomerProfile from './CustomerProfile';

import {
  FileText,
  PlusSquare,
  Briefcase,
  UserCircle,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
  Store,
  Users,
} from 'lucide-react';

const NAV_ITEMS = [
  { key: 'Generated JDs', label: "Generated JD's", icon: FileText },
  { key: 'Job Posting', label: 'Post a Job', icon: PlusSquare },
  { key: 'Posted Jobs', label: 'My Posted Jobs', icon: Briefcase },
  { key: 'Master Inbox', label: 'Master Inbox', icon: Users },
  { key: 'Profile', label: 'Company Profile', icon: UserCircle },
];

const CustomerDashboard = () => {
  const [activeTab, setActiveTab] = useState('Posted Jobs');
  const [customerName, setCustomerName] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [viewingJob, setViewingJob] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const storedName = localStorage.getItem('customerName');
    const storedId = localStorage.getItem('customerId');
    const storedCompany = localStorage.getItem('companyName');

    if (storedName) setCustomerName(storedName);
    if (storedId) setCustomerId(storedId);
    if (storedCompany) setCompanyName(storedCompany);

    const queryParams = new URLSearchParams(location.search);
    const tabParam = queryParams.get('tab');

    if (tabParam === 'generator') {
      setActiveTab('JD Generator');
    }
  }, [location]);

  const handleLogout = () => {
    localStorage.clear();
    navigate('/');
  };

  // 🚨 FIX: Always reset viewingJob when clicking any sidebar tab
  const handleTabClick = (tabName) => {
    setActiveTab(tabName);
    setViewingJob(null);
    if (location.search) {
      navigate('/customer-dashboard');
    }
  };

  const renderContent = () => {
    if (viewingJob) {
      return (
        <RecruiterApplicants
          jobId={viewingJob.jobId}
          jobRole={viewingJob.jobRole}
          posterRole={viewingJob.posterRole}
          onBack={() => setViewingJob(null)}
        />
      );
    }

    switch (activeTab) {
      case 'JD Generator':
        return <JDGenerator creatorId={customerId} />;
      case 'Generated JDs':
        return <CustomerGeneratedJDs customerId={customerId} />;
      case 'Job Posting':
        return <JobPosting onPostSuccess={setActiveTab} providedId={customerId} />;
      case 'Posted Jobs':
        return (
          <PostedJobs
            onEditClick={setActiveTab}
            onViewApplicants={setViewingJob}
            providedId={customerId}
          />
        );
      case 'Master Inbox':
        return <MasterApplicants isRecruiter={false} providedId={customerId} />;
      case 'Profile':
        return <CustomerProfile />;
      default:
        return (
          <PostedJobs
            onEditClick={setActiveTab}
            onViewApplicants={setViewingJob}
            providedId={customerId}
          />
        );
    }
  };

  const getInitials = (name) => {
    if (!name) return 'C';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return parts[0][0].toUpperCase();
  };

  const displayCompanyName = (name) => {
    if (!name) return 'Customer Portal';
    if (name.length > 22) return name.substring(0, 22) + '…';
    return name;
  };

  return (
    <div className="cu-layout">
      {/* ═══ Sidebar ═══ */}
      <aside className={`cu-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
        {/* Top — Brand + Collapse */}
        <div className="cu-sidebar-top">
          <div
            className="cu-sidebar-brand"
            onClick={() => sidebarCollapsed && setSidebarCollapsed(false)}
          >
            <div className="cu-brand-icon">
              <Store size={22} />
            </div>
            {!sidebarCollapsed && (
              <div className="cu-brand-text">
                <span
                  className="cu-brand-name"
                  title={companyName || customerName || 'Customer Portal'}
                >
                  {displayCompanyName(companyName || customerName)}
                </span>
                <span className="cu-brand-sub">Employer Console</span>
              </div>
            )}
          </div>

          {!sidebarCollapsed && (
            <button
              className="cu-collapse-btn"
              onClick={() => setSidebarCollapsed(true)}
              title="Collapse sidebar"
            >
              <PanelLeftClose size={18} />
            </button>
          )}
        </div>

        {/* Expand button when collapsed */}
        {sidebarCollapsed && (
          <button
            className="cu-collapse-btn cu-expand-btn"
            onClick={() => setSidebarCollapsed(false)}
            title="Expand sidebar"
          >
            <PanelLeftOpen size={18} />
          </button>
        )}

        {/* Navigation */}
        <nav className="cu-sidebar-nav">
          {NAV_ITEMS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              className={`cu-nav-item ${
                activeTab === key && !viewingJob ? 'active' : ''
              }`}
              onClick={() => handleTabClick(key)}
              title={sidebarCollapsed ? label : ''}
            >
              <Icon size={20} className="cu-nav-icon" />
              {!sidebarCollapsed && (
                <span className="cu-nav-label">{label}</span>
              )}
              {!sidebarCollapsed && activeTab === key && !viewingJob && (
                <span className="cu-nav-indicator" />
              )}
            </button>
          ))}
        </nav>

        {/* Footer */}
        <div className="cu-sidebar-footer">
          {!sidebarCollapsed && (
            <div
              className="cu-user-card"
              onClick={() => handleTabClick('Profile')}
            >
              <div className="cu-user-avatar">{getInitials(customerName)}</div>
              <div className="cu-user-info">
                <span className="cu-user-name" title={customerName}>
                  {customerName
                    ? customerName.length > 16
                      ? customerName.substring(0, 16) + '…'
                      : customerName
                    : 'Customer'}
                </span>
                <span className="cu-user-role">Employer</span>
              </div>
            </div>
          )}

          {sidebarCollapsed && (
            <div
              className="cu-user-avatar cu-user-avatar-collapsed"
              onClick={() => handleTabClick('Profile')}
              title={customerName || 'Profile'}
            >
              {getInitials(customerName)}
            </div>
          )}

          <button
            className="cu-logout-btn"
            onClick={handleLogout}
            title="Log out"
          >
            <LogOut size={18} />
            {!sidebarCollapsed && <span>Log Out</span>}
          </button>
        </div>
      </aside>

      {/* ═══ Main Content ═══ */}
      <main className="cu-main">
        <div className="cu-content">{renderContent()}</div>
      </main>
    </div>
  );
};

export default CustomerDashboard;